import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { EmailCampaign } from '../core/_models_email';
import { User } from '../core/_models';
import { useAxios } from '../core/_axios';

interface TrainingProgress {
  completed_count: number;
  required_count: number;
  is_complete: boolean;
  remaining: number;
}

interface EmailReviewModalProps {
  campaign: EmailCampaign | null;
  isOpen: boolean;
  onClose: () => void;
  onApprove: (campaignId: string, updatedContent?: string | { email_content: string; email_subject: string }) => void;
  onReject: (campaignId: string, reason: string) => void;
  currentUser: User | null;
  trainingProgress?: TrainingProgress | null;
}

const EmailReviewModal: React.FC<EmailReviewModalProps> = ({
  campaign,
  isOpen,
  onClose,
  onApprove,
  onReject,
  currentUser,
  trainingProgress
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [editedSubject, setEditedSubject] = useState('');
  const [showRejectReason, setShowRejectReason] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showSignerDetails, setShowSignerDetails] = useState(false);
  const [account, setAccount] = useState<any>(null);
  const [showAIDetails, setShowAIDetails] = useState(false);
  const axios = useAxios();

  useEffect(() => {
    if (campaign) {
      setEditedContent(campaign.final_email_content || '');
      setEditedSubject(campaign.email_subject || '');
    }
  }, [campaign]);

  useEffect(() => {
    const fetchAccount = async () => {
      try {
        // If campaign has account_unique_identifier, use it to fetch the specific account
        if (campaign?.account_unique_identifier) {
          console.log('Fetching account for campaign:', campaign.account_unique_identifier);
          // Fetch the specific account that created this campaign
          const response = await axios.get(`/authenticated/management/accounts/${campaign.account_unique_identifier}`);
          console.log('Account fetch response:', response.data);
          // Management endpoint returns data directly without success field
          if (response.data.data?.account) {
            const accountData = response.data.data.account;
            console.log('Fetched campaign account data:', accountData);
            console.log('Account public_data:', accountData.public_data);
            console.log('Email signature enabled:', accountData.public_data?.email_signature_enabled);
            console.log('Email signature:', accountData.public_data?.email_signature);
            setAccount(accountData);
          } else {
            console.log('Account data not found in response');
          }
        } else {
          // Only try to fetch current account if not viewing all accounts
          // Check if we have an account context first
          try {
            const response = await axios.get('/authenticated/current-account/get-current-account');
            if (response.data.success) {
              console.log('Fetched current account data:', response.data.data.account);
              setAccount(response.data.data.account);
            }
          } catch (error) {
            // This is expected when viewing all accounts - just log debug message
            console.log('No current account available (viewing all accounts)');
          }
        }
      } catch (error) {
        console.error('Failed to fetch account:', error);
        // Don't try fallback if we're viewing all accounts
      }
    };
    
    if (isOpen && campaign) {
      fetchAccount();
    }
  }, [isOpen, campaign, axios]);

  if (!isOpen || !campaign) return null;

  // Check if campaign is still generating
  const isGenerating = campaign.status === 'generating' || (!campaign.final_email_content && !campaign.email_subject);
  
  // Debug logging
  console.log('EmailReviewModal render state:', { 
    isGenerating, 
    hasAccount: !!account,
    accountName: account?.name,
    signatureEnabled: account?.public_data?.email_signature_enabled,
    signature: account?.public_data?.email_signature,
    campaignData: {
      hasAIPrompt: !!campaign.ai_prompt,
      hasAIResponse: !!campaign.ai_response,
      modelUsed: campaign.model_used,
      accountUniqueId: campaign.account_unique_identifier
    }
  });

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      // Could add a toast notification here
      console.log(`${label} copied to clipboard`);
    });
  };

  const formatJSON = (jsonString: string) => {
    try {
      const parsed = JSON.parse(jsonString);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return jsonString; // Return as-is if not valid JSON
    }
  };

  const hasUnsavedChanges = () => {
    if (!isEditing) return false;
    return (editedContent !== campaign.final_email_content || editedSubject !== campaign.email_subject);
  };

  const handleClose = () => {
    if (hasUnsavedChanges()) {
      if (confirm('You have unsaved changes. Are you sure you want to close without saving?')) {
        setIsEditing(false);
        setEditedContent(campaign.final_email_content || '');
        setEditedSubject(campaign.email_subject || '');
        onClose();
      }
    } else {
      onClose();
    }
  };

  const handleApprove = () => {
    const contentToSend = isEditing ? {
      email_content: editedContent,
      email_subject: editedSubject
    } : undefined;
    onApprove(campaign.id, contentToSend);
    setIsEditing(false);
    onClose();
  };

  const handleReject = () => {
    onReject(campaign.id, rejectReason || 'Rejected by admin');
    setIsEditing(false);
    onClose();
  };

  const getLeadScoreBadgeClass = (score: number) => {
    if (score >= 80) return 'badge-success';
    if (score >= 60) return 'badge-warning';
    if (score >= 40) return 'badge-info';
    return 'badge-light';
  };

  return (
    <>
      {/* Backdrop */}
      <div className="transition-all duration-300 fixed inset-0 bg-gray-900 opacity-25 z-40" onClick={handleClose}></div>
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b">
            <div className="flex items-center gap-4">
              <h3 className="text-xl font-semibold text-gray-900">Review Email Campaign</h3>
              <div className="flex items-center gap-2">
                <span className={`badge ${getLeadScoreBadgeClass(campaign.lead_score)}`}>
                  Score: {campaign.lead_score}
                </span>
                {campaign.is_dry_run && (
                  <span className="badge badge-light-warning">Dry Run</span>
                )}
                {campaign.lead_score_breakdown?.interested_in_pre_need > 0 && (
                  <span className="badge badge-light-primary">Pre-Need</span>
                )}
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <i className="ki-filled ki-cross text-2xl"></i>
            </button>
          </div>
          
          {/* Training Progress Banner */}
          {trainingProgress && !trainingProgress.is_complete && (
            <div className="bg-blue-50 border-b border-blue-200 px-6 py-3">
              <div className="flex items-center">
                <i className="ki-filled ki-information-5 text-blue-600 text-lg mr-2"></i>
                <div className="text-sm">
                  <strong className="text-blue-800">Training Mode:</strong>{' '}
                  <span className="text-blue-700">
                    {trainingProgress.remaining} more message{trainingProgress.remaining !== 1 ? 's' : ''} need{trainingProgress.remaining === 1 ? 's' : ''} to be approved before automatic suggestions begin.
                  </span>
                  <span className="text-blue-600 ml-2">
                    Progress: {trainingProgress.completed_count}/{trainingProgress.required_count} messages sent
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Compact single-line summary */}
            <div className="bg-gray-50 rounded-lg mb-4">
              <div 
                className="p-3 cursor-pointer hover:bg-gray-100 transition-colors flex items-center justify-between"
                onClick={() => setShowSignerDetails(!showSignerDetails)}
              >
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-600">From:</span>
                  <span className="font-medium">{campaign.signer_info?.name || 'Unknown'}</span>
                  <span className="text-gray-400">•</span>
                  <span className="text-gray-600">For:</span>
                  <span className="font-medium">{campaign.deceased_info?.name || campaign.service_name || 'Unknown'}</span>
                  <span className="text-gray-400">•</span>
                  <span className="text-gray-600">{campaign.signer_info?.email || 'No email'}</span>
                </div>
                <i className={`ki-filled ${showSignerDetails ? 'ki-up' : 'ki-down'} text-gray-400`}></i>
              </div>
              
              {/* Expandable details */}
              {showSignerDetails && (
                <div className="p-3 pt-0">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h5 className="font-semibold text-gray-700 text-xs mb-2 uppercase">Signer Details</h5>
                      <p className="text-sm"><strong>{campaign.signer_info?.name || 'Unknown'}</strong></p>
                      <p className="text-sm text-gray-600">{campaign.signer_info?.email || 'No email'}</p>
                      {campaign.signer_info?.phone && (
                        <p className="text-sm text-gray-600">{campaign.signer_info.phone}</p>
                      )}
                      {campaign.signer_info?.relationship && (
                        <p className="text-sm text-gray-600">Relationship: {campaign.signer_info.relationship}</p>
                      )}
                      {campaign.signer_info?.address && (
                        <p className="text-sm text-gray-600">{campaign.signer_info.address}</p>
                      )}
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-700 text-xs mb-2 uppercase">Service Details</h5>
                      <p className="text-sm"><strong>{campaign.deceased_info?.name || campaign.service_name || 'Unknown'}</strong></p>
                      {campaign.service_unique_identifier && (
                        <p className="text-sm text-gray-600">ID: {campaign.service_unique_identifier}</p>
                      )}
                    </div>
                  </div>
                  
                  {/* AI Details - always visible when available */}
                  {campaign.ai_prompt && currentUser?.email === 'chris@cohoat.tech' && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <button
                        onClick={() => setShowAIDetails(!showAIDetails)}
                        className="flex items-center justify-between mb-2 w-full hover:opacity-80 transition-opacity"
                      >
                        <div className="font-semibold text-gray-700 text-xs uppercase flex items-center hover:text-blue-600">
                          <i className={`ki-filled ${showAIDetails ? 'ki-down' : 'ki-right'} mr-1`}></i>
                          <i className="ki-filled ki-cpu mr-1"></i>
                          AI Generation Details
                        </div>
                        {campaign.model_used && (
                          <span className="badge badge-light-primary badge-sm">
                            <i className="ki-filled ki-technology text-xs mr-1"></i>
                            {campaign.model_used}
                          </span>
                        )}
                      </button>
                      
                      {showAIDetails && (
                        <div className="p-3">
                          {campaign.ai_response && (() => {
                            // Try to parse the AI response to show raw API data
                            let apiData = null;
                            try {
                              const parsed = JSON.parse(campaign.ai_response);
                              if (parsed.request || parsed.response || parsed.prompt) {
                                apiData = parsed;
                              }
                            } catch {}
                            
                            if (apiData) {
                              // New format with complete API data
                              return (
                                <>
                                  {apiData.request?.input && (
                                    <div className="mb-3">
                                      <div className="flex items-center justify-between mb-1">
                                        <h6 className="text-xs font-medium text-gray-600">Prompt (from API Request):</h6>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleCopy(apiData.request.input, 'Prompt');
                                          }}
                                          className="text-xs text-blue-600 hover:text-blue-700 underline"
                                        >
                                          Copy
                                        </button>
                                      </div>
                                      <div className="bg-gray-50 p-2 rounded border text-xs font-mono whitespace-pre-wrap overflow-x-auto max-h-64 overflow-y-auto select-text">
                                        {apiData.request.input}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {apiData.request && (
                                    <div className="mb-3">
                                      <div className="flex items-center justify-between mb-1">
                                        <h6 className="text-xs font-medium text-gray-600">API Request (excluding prompt shown above):</h6>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            // Create a copy without the input field for cleaner display
                                            const requestWithoutInput = { ...apiData.request };
                                            delete requestWithoutInput.input;
                                            handleCopy(JSON.stringify(apiData.request, null, 2), 'Full API Request');
                                          }}
                                          className="text-xs text-blue-600 hover:text-blue-700 underline"
                                        >
                                          Copy Full
                                        </button>
                                      </div>
                                      <div className="bg-white p-2 rounded border text-xs font-mono whitespace-pre-wrap overflow-x-auto max-h-48 overflow-y-auto select-text">
                                        {(() => {
                                          const requestDisplay = { ...apiData.request };
                                          requestDisplay.input = "<SEE PROMPT SECTION ABOVE>";
                                          return JSON.stringify(requestDisplay, null, 2);
                                        })()}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {apiData.response && (
                                    <div>
                                      <div className="flex items-center justify-between mb-1">
                                        <h6 className="text-xs font-medium text-gray-600">API Response:</h6>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleCopy(JSON.stringify(apiData.response, null, 2), 'API Response');
                                          }}
                                          className="text-xs text-blue-600 hover:text-blue-700 underline"
                                        >
                                          Copy
                                        </button>
                                      </div>
                                      <div className="bg-white p-2 rounded border text-xs font-mono whitespace-pre-wrap overflow-x-auto max-h-48 overflow-y-auto select-text">
                                        {JSON.stringify(apiData.response, null, 2)}
                                      </div>
                                    </div>
                                  )}
                                </>
                              );
                            } else {
                              // Legacy format - just show the raw response
                              return (
                                <div>
                                  <div className="flex items-center justify-between mb-1">
                                    <h6 className="text-xs font-medium text-gray-600">AI Data:</h6>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCopy(formatJSON(campaign.ai_response), 'AI Data');
                                      }}
                                      className="text-xs text-blue-600 hover:text-blue-700 underline"
                                    >
                                      Copy
                                    </button>
                                  </div>
                                  <div className="bg-white p-2 rounded border text-xs font-mono whitespace-pre-wrap overflow-x-auto max-h-48 overflow-y-auto select-text">
                                    {formatJSON(campaign.ai_response)}
                                  </div>
                                </div>
                              );
                            }
                          })()}
                          
                          {campaign.ai_prompt && !campaign.ai_response && (
                            <div className="mb-3">
                              <div className="flex items-center justify-between mb-1">
                                <h6 className="text-xs font-medium text-gray-600">Prompt Used:</h6>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCopy(campaign.ai_prompt, 'AI Prompt');
                                  }}
                                  className="text-xs text-blue-600 hover:text-blue-700 underline"
                                >
                                  Copy
                                </button>
                              </div>
                              <div className="bg-white p-2 rounded border text-xs font-mono whitespace-pre-wrap overflow-x-auto max-h-48 overflow-y-auto select-text">
                                {campaign.ai_prompt}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="border rounded">
              <div className="flex justify-between items-center p-3 border-b bg-gray-50">
                <div className="flex items-center gap-3">
                  <h4 className="font-semibold text-gray-700">Email Content</h4>
                  {campaign.model_used && (
                    <span className="badge badge-light-primary badge-sm">
                      <i className="ki-filled ki-technology text-xs mr-1"></i>
                      {campaign.model_used}
                    </span>
                  )}
                </div>
                {!isGenerating && (
                  <button
                    onClick={() => setIsEditing(!isEditing)}
                    className="btn btn-sm btn-light"
                  >
                    <i className={`ki-filled ${isEditing ? 'ki-check' : 'ki-pencil'} mr-2`}></i>
                    {isEditing ? 'Done' : 'Edit'}
                  </button>
                )}
              </div>
              
              <div className="p-4">
                {isGenerating ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
                    <p className="text-gray-600 mb-2">Generating personalized message...</p>
                    <p className="text-sm text-gray-500">This typically takes 10-20 seconds</p>
                  </div>
                ) : (
                  <>
                    <div className="mb-3">
                      <span className="text-sm font-medium text-gray-700">To:</span>
                      <span className="text-sm ml-2">{campaign.signer_info?.email || 'No email'}</span>
                    </div>
                    <div className="mb-3">
                      <span className="text-sm font-medium text-gray-700">Subject:</span>
                      {isEditing ? (
                        <input
                          type="text"
                          className="w-full mt-2 p-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={editedSubject}
                          onChange={(e) => setEditedSubject(e.target.value)}
                        />
                      ) : (
                        <span className="text-sm ml-2">{campaign.email_subject}</span>
                      )}
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-700">Message:</span>
                      {isEditing ? (
                        <textarea
                          className="w-full mt-2 p-3 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          rows={8}
                          value={editedContent}
                          onChange={(e) => setEditedContent(e.target.value)}
                        />
                      ) : (
                        <div className="mt-2 text-sm whitespace-pre-wrap bg-gray-50 p-3 rounded">{campaign.final_email_content}</div>
                      )}
                    </div>
                    
                    {/* Signature Preview */}
                    {!isGenerating && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">Email Signature:</span>
                          <Link 
                            to={campaign?.account_unique_identifier 
                              ? `/management/accounts/${campaign.account_unique_identifier}/email-settings`
                              : `/manage-account/email-settings`} 
                            className="text-xs text-blue-600 hover:text-blue-700 underline"
                          >
                            {account?.public_data?.email_signature_enabled === false ? 'Enable signature' : 'Edit signature'}
                          </Link>
                        </div>
                        {!account ? (
                          <div className="text-sm text-gray-500">Loading signature settings...</div>
                        ) : account.public_data?.email_signature_enabled === false ? (
                          <div className="text-sm text-gray-500 italic">
                            Signature is disabled. <Link 
                              to={campaign?.account_unique_identifier 
                                ? `/management/accounts/${campaign.account_unique_identifier}/email-settings`
                                : `/manage-account/email-settings`} 
                              className="text-blue-600 hover:text-blue-700 underline">Enable it in settings</Link> to add a signature to your emails.
                          </div>
                        ) : (
                          <div className="text-sm whitespace-pre-wrap bg-gray-50 p-3 rounded">
                            {account.public_data?.email_signature || `The ${account.name} Team`}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          {showRejectReason ? (
            <div className="p-6 border-t">
              <h4 className="font-semibold text-gray-700 mb-2">Rejection Reason</h4>
              <textarea
                className="w-full p-3 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-red-500 mb-4"
                rows={2}
                placeholder="Enter reason for rejection (optional)"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowRejectReason(false);
                    setRejectReason('');
                  }}
                  className="btn btn-light"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  className="btn btn-danger"
                >
                  Confirm Rejection
                </button>
              </div>
            </div>
          ) : (
            <div className="flex justify-end gap-3 p-6 border-t">
              <button
                onClick={() => setShowRejectReason(true)}
                className="btn btn-lg btn-light"
                disabled={isGenerating}
              >
                <i className="ki-filled ki-cross mr-2"></i>
                Skip
              </button>
              <button
                onClick={handleApprove}
                className="btn btn-lg btn-success"
                disabled={isGenerating}
              >
                <i className="ki-filled ki-check mr-2"></i>
                {isGenerating ? 'Generating...' : 'Approve & Send'}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EmailReviewModal;